import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';

import authReducer from './slices/authSlice';
import personImageReducer, { persistPersonImageState } from './slices/personImageSlice';
import backgroundReducer, { persistBackgroundState } from './slices/backgroundSlice';
import vtoReducer from './slices/vtoSlice';
import clothingItemsReducer from './slices/clothingItemsSlice';
import creditReducer from './slices/creditSlice';
import { persistVTOState, loadVTOHistoryOnStart } from './middleware/vtoStorageMiddleware';
import { personImageVTOSyncMiddleware } from './middleware/personImageVTOSyncMiddleware';
import { VTOStorage } from '../utils/vtoStorage';
import { enhancedClosetApi } from './apis/endpoints/closetApi';
import { enhancedPersonImageApi } from './apis/endpoints/personImageApi';
import { enhancedBackgroundImageApi } from './apis/endpoints/backgroundImageApi';
import { enhancedVTOResultApi } from './apis/endpoints/vtoResultApi';
import { enhancedCreditApi } from './apis/endpoints/creditApi';
import { enhancedTopPicksApi } from './apis/endpoints/topPicksApi';
import { enhancedLookbookApi } from './apis/endpoints/lookbookApi';
import { enhancedAuthApi } from './apis/endpoints/authApi';
import { enhancedSharedResultApi } from './apis/endpoints/sharedResultApi';
import { enhancedShareBundleApi } from './apis/endpoints/shareBundleApi';
import { enhancedLicensingApi } from './apis/endpoints/licensingApi';
import { enhancedPaymentApi } from './apis/endpoints/paymentApi';
import { enhancedSharedClosetApi } from './apis/endpoints/sharedClosetApi';
import { enhancedEarningsApi } from './apis/endpoints/earningsApi';
import { enhancedSharedProfileApi } from './apis/endpoints/sharedProfileApi';
import ambassadorApi from './apis/endpoints/ambassadorApi';
import VirtualTryOnService from '../services/virtualTryOnService';

export const store = configureStore({
  reducer: {
    auth: authReducer,
    personImage: personImageReducer,
    background: backgroundReducer,
    vto: vtoReducer,
    clothingItems: clothingItemsReducer,
    credit: creditReducer,
    // Add RTK Query API reducers
    [enhancedClosetApi.reducerPath]: enhancedClosetApi.reducer,
    [enhancedPersonImageApi.reducerPath]: enhancedPersonImageApi.reducer,
    [enhancedBackgroundImageApi.reducerPath]: enhancedBackgroundImageApi.reducer,
    [enhancedVTOResultApi.reducerPath]: enhancedVTOResultApi.reducer,
    [enhancedCreditApi.reducerPath]: enhancedCreditApi.reducer,
    [enhancedTopPicksApi.reducerPath]: enhancedTopPicksApi.reducer,
    [enhancedLookbookApi.reducerPath]: enhancedLookbookApi.reducer,
    [enhancedAuthApi.reducerPath]: enhancedAuthApi.reducer,
    [enhancedSharedResultApi.reducerPath]: enhancedSharedResultApi.reducer,
    [enhancedShareBundleApi.reducerPath]: enhancedShareBundleApi.reducer,
    [enhancedLicensingApi.reducerPath]: enhancedLicensingApi.reducer,
    [enhancedPaymentApi.reducerPath]: enhancedPaymentApi.reducer,
    [enhancedSharedClosetApi.reducerPath]: enhancedSharedClosetApi.reducer,
    [enhancedEarningsApi.reducerPath]: enhancedEarningsApi.reducer,
    [enhancedSharedProfileApi.reducerPath]: enhancedSharedProfileApi.reducer,
    [ambassadorApi.reducerPath]: ambassadorApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
    })
      .concat(enhancedClosetApi.middleware)
      .concat(enhancedPersonImageApi.middleware)
      .concat(enhancedBackgroundImageApi.middleware)
      .concat(enhancedVTOResultApi.middleware)
      .concat(enhancedCreditApi.middleware)
      .concat(enhancedTopPicksApi.middleware)
      .concat(enhancedLookbookApi.middleware)
      .concat(enhancedAuthApi.middleware)
      .concat(enhancedSharedResultApi.middleware)
      .concat(enhancedShareBundleApi.middleware)
      .concat(enhancedLicensingApi.middleware)
      .concat(enhancedPaymentApi.middleware)
      .concat(enhancedSharedClosetApi.middleware)
      .concat(enhancedEarningsApi.middleware)
      .concat(enhancedSharedProfileApi.middleware)
      .concat(ambassadorApi.middleware)
      .concat(persistPersonImageState, persistBackgroundState, persistVTOState, personImageVTOSyncMiddleware),
  devTools: process.env.NODE_ENV !== 'production',
});

setupListeners(store.dispatch);

// Load VTO history on app start
loadVTOHistoryOnStart(store);

// Initialize VTO service with store reference for RTK Query integration
VirtualTryOnService.setStore(store);

// Expose VTOStorage for debugging in development
if (process.env.NODE_ENV === 'development') {
  window.VTOStorage = VTOStorage;
  window.store = store;

  // Helper function to test VTO persistence
  window.testVTOPersistence = () => {
    console.log('[Store] Testing VTO Persistence...');

    // Test the storage utility
    const storageTest = VTOStorage.testVTOStorage();

    // Test setting original person image (WEARER)
    store.dispatch({
      type: 'vto/setOriginalPersonImage',
      payload: 'data:image/jpeg;base64,originalwearer123'
    });

    // Test setting active clothing items (WEARING)
    store.dispatch({
      type: 'clothingItems/setActiveItems',
      payload: [{
        id: 'test_item_123',
        name: 'Test Garment',
        image: 'data:image/jpeg;base64,testgarment123'
      }]
    });

    // Test dispatching a VTO generation success action
    store.dispatch({
      type: 'vto/generationSuccess',
      payload: {
        imageUrl: 'data:image/jpeg;base64,testimage123',
        clothingItems: [{
          id: 'test_item_123',
          name: 'Test Garment',
          image: 'data:image/jpeg;base64,testgarment123'
        }]
      }
    });

    console.log('[Store] Test VTO action dispatched');

    // Check localStorage after dispatch
    setTimeout(() => {
      console.log('[Store] localStorage after dispatch:');
      console.log('vto_history:', localStorage.getItem('vto_history'));
      console.log('vto_enhanced_state:', localStorage.getItem('vto_enhanced_state'));
    }, 100);

    return storageTest;
  };

  // Helper to check wearer and wearing persistence
  window.checkWearerAndWearing = () => {
    console.log('[Store] Checking Wearer and Wearing Persistence...');
    const wearerWearing = VTOStorage.getWearerAndWearing();
    console.log('Current wearer and wearing state:', wearerWearing);
    return wearerWearing;
  };

  // Helper to check localStorage usage
  window.checkStorageUsage = () => {
    console.log('[Store] Checking localStorage usage...');
    let totalSize = 0;
    const items = {};

    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        const size = localStorage[key].length;
        totalSize += size;
        items[key] = {
          size: size,
          sizeKB: Math.round(size / 1024),
          sizeMB: Math.round(size / 1024 / 1024 * 100) / 100
        };
      }
    }

    const result = {
      totalSize,
      totalKB: Math.round(totalSize / 1024),
      totalMB: Math.round(totalSize / 1024 / 1024 * 100) / 100,
      items: Object.entries(items)
        .sort((a, b) => b[1].size - a[1].size)
        .slice(0, 10) // Top 10 largest items
    };

    console.log('Storage usage:', result);
    return result;
  };

  // Helper to clear VTO data and free up space
  window.clearVTOStorage = () => {
    console.log('[Store] Clearing VTO storage...');
    VTOStorage.clearAllVTOData();
    VTOStorage.clearLargeStorageItems();
    console.log('[Store] VTO storage cleared');
  };
}

export default store;